import { useCallback, useSyncExternalStore } from 'react';
import { Comment, InlineCommentClickHandler } from '@amzn/awscat-editor';
import {
  InlineCommentCancelledHandler,
  InlineCommentProposedHandler,
} from '@amzn/awscat-editor/dist/extensions/commentProposal';
import { Editor } from '@tiptap/core';

import { useCanStartCommentThread } from '../../v2-hooks/useCanStartCommentThread';
import { useInlineCommentManager } from '../../../contexts/InlineCommentManagerContext';
import { CommentPositionUpdate } from '../../../services/InlineCommentManager';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { DEFAULT_INLINE_TEXT_FILTER, PopoverState } from './Constants';
import { useInlineCommentIntegration } from './useInlineCommentIntegration';
import {
  CommentProposalState,
  useCommentProposalIntegration,
} from './useCommentProposalIntegration';

export interface TiptapEditorViewingModeViewModelProps {
  readonly inlineTextFilter: InlineTextFilter | undefined;
  readonly editor: Editor | undefined;
}

export interface TiptapEditorViewingModeViewModel {
  readonly isCommentingModeEnabled: boolean;
  readonly editorComments: Comment[] | undefined;
  readonly stageCommentPositions: (comments: CommentPositionUpdate[]) => void;
  readonly handleInlineCommentClick: InlineCommentClickHandler;
  readonly popover: PopoverState | undefined;
  readonly hidePopover: () => void;
  readonly commentProposalState: CommentProposalState | undefined;
  readonly handleProposed: InlineCommentProposedHandler;
  readonly handleCancelled: InlineCommentCancelledHandler;
  readonly dismissProposal: () => void;
  readonly submitComment: (body: string) => Promise<void>;
}

export function useTiptapEditorViewingModeViewModel(
  props: TiptapEditorViewingModeViewModelProps,
): TiptapEditorViewingModeViewModel {
  const commentManager = useInlineCommentManager();

  // When the commenting display toggle flips, then re-read it.
  const subscribeToCommentingMode = useCallback(
    (onStoreChange: () => void) => commentManager.on('viewCommentingModeChanged', onStoreChange),
    [commentManager],
  );
  const isViewCommentingEnabled = useSyncExternalStore(
    subscribeToCommentingMode,
    () => commentManager.isViewCommentingEnabled,
  );

  // Commenting needs the display toggle on and permission to open a thread —
  // only reviewers, and only while the content is still a draft.
  const canStartCommentThread = useCanStartCommentThread();
  const isCommentingModeEnabled = isViewCommentingEnabled && canStartCommentThread;

  // When commenting is disabled, use a 'none' filter so the integration loads nothing.
  const effectiveFilter = isCommentingModeEnabled
    ? props.inlineTextFilter
    : DEFAULT_INLINE_TEXT_FILTER;

  const inlineCommentIntegration = useInlineCommentIntegration(props.editor, effectiveFilter);

  const hidePopover = inlineCommentIntegration.hidePopover;
  const onHideOtherPopovers = useCallback(() => {
    hidePopover();
  }, [hidePopover]);

  const commentProposalIntegration = useCommentProposalIntegration(
    props.editor,
    effectiveFilter,
    onHideOtherPopovers,
  );

  return {
    isCommentingModeEnabled,
    editorComments: inlineCommentIntegration.editorComments,
    stageCommentPositions: inlineCommentIntegration.stageCommentPositions,
    handleInlineCommentClick: inlineCommentIntegration.handleInlineCommentClick,
    popover: inlineCommentIntegration.popover,
    hidePopover,
    commentProposalState: commentProposalIntegration.proposalState,
    handleProposed: commentProposalIntegration.handleProposed,
    handleCancelled: commentProposalIntegration.handleCancelled,
    dismissProposal: commentProposalIntegration.dismissProposal,
    submitComment: commentProposalIntegration.submitComment,
  };
}
